import { useRecoilState, useRecoilValue } from "recoil";
import { progressCheckDataAtom, userAtom } from "@state/State";
import { AllProgressCheckStatuses, IUser, ProgramData } from "@interfaces/Interfaces";
import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { DATA_REFRESH_INTERVAL } from "../../Constants";
import * as ProgramUtils from "@utils/programUtils";
import * as ProgressCheckService from "@controllers/ProgressCheckService";
import { useProgramData } from "./useProgramData";
import { useLoggedIn } from "@hooks/auth/useLoggedIn";

type RefreshProgressCheckData = {
  refreshProgressCheckData: (user: IUser) => Promise<AllProgressCheckStatuses[]>;
};

const getUserProgramsIdentifier = (userPrograms: ProgramData[]): string => {
  if (!userPrograms || userPrograms.length === 0) {
    return "none";
  }
  const programIDs = userPrograms.map((program) => program.ID).sort();
  return programIDs.join(",");
};

const fetchProgressCheckData = async (
  user: IUser,
  userPrograms: ProgramData[]
): Promise<AllProgressCheckStatuses[]> => {
  if (!user || !user.userData) {
    throw new Error("User is required");
  }

  const programIDs = ProgramUtils.getProgramIDsFromSubscriptions(user.userData.subscriptions, userPrograms);
  const progressCheckProgramIDs = ProgramUtils.filterProgressCheckProgramIDs(programIDs);

  if (!progressCheckProgramIDs?.length) {
    return [];
  }

  const data =
    (await Promise.all(
      progressCheckProgramIDs.map(async (programID) => {
        const result = await ProgressCheckService.getAllProgressCheckStatuses(user, programID);
        return { ...result, programID };
      })
    )) ?? [];

  return data;
};

export const useRefreshProgressCheckData = (shouldLoad?: boolean): RefreshProgressCheckData => {
  const user = useRecoilValue(userAtom);
  const [progressCheckData, setProgressCheckData] = useRecoilState<AllProgressCheckStatuses[]>(progressCheckDataAtom);
  const loggedIn = useLoggedIn();
  const queryClient = useQueryClient();

  const { userPrograms } = useProgramData();

  const userProgramsIdentifier = useMemo(() => getUserProgramsIdentifier(userPrograms), [userPrograms]);

  const isEnabled = Boolean(loggedIn && user && user.userData && userPrograms.length > 0 && shouldLoad);

  const { data: refreshedData } = useQuery({
    queryKey: ["progress-check-data", user?.userData?.contactID, userProgramsIdentifier],
    queryFn: () => fetchProgressCheckData(user!, userPrograms),
    enabled: isEnabled,
    refetchInterval: isEnabled ? DATA_REFRESH_INTERVAL : false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!user || !user.userData) {
      queryClient.removeQueries({ queryKey: ["progress-check-data"] });
    }
  }, [user, queryClient]);

  useEffect(() => {
    if (refreshedData) {
      const isEqual = _.isEqual(progressCheckData, refreshedData);

      if (!isEqual) {
        setProgressCheckData(refreshedData);
      }
    }
  }, [refreshedData, progressCheckData, setProgressCheckData]);

  const refreshProgressCheckData = useCallback(
    async (_user: IUser): Promise<AllProgressCheckStatuses[]> => {
      try {
        if (!_user || !_user.userData) {
          return [];
        }

        const data = await fetchProgressCheckData(_user, userPrograms);
        const isEqual = _.isEqual(progressCheckData, data);

        if (!isEqual) {
          setProgressCheckData(data);
        }

        const identifier = getUserProgramsIdentifier(userPrograms);
        queryClient.invalidateQueries({
          queryKey: ["progress-check-data", _user.userData.contactID, identifier],
        });

        return data;
      } catch (error) {
        console.log(error);
        return [];
      }
    },
    [userPrograms, progressCheckData, setProgressCheckData, queryClient]
  );

  return {
    refreshProgressCheckData,
  };
};
